import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { Card } from '../components/common/Card';
import { Text } from '../components/common/Typography';
import { BackButton, MobileSearchField } from '../components/common';
import GameHistoryTable from '../components/player/GameHistoryTable';
import Tabs from '../components/common/Tabs';
import { fetchSessionLeaderboardSnapshotRecap } from '../services/leaderboardSnapshotService';
import { enrichSessionLadderSnapshot } from '../services/homeDashboardService';
import { SessionPerformanceHero } from '../components/session/SessionPerformanceHero';
import SessionRankingsTable from '../components/session/SessionRankingsTable';
import {
  buildRecentFormString,
  buildSessionLadderSnapshot,
  summarizeLadderGameResults,
} from '../utils/sessionSnapshotUtils';
import {
  buildEloByGamePlayerMap,
  buildPlayerGameHistoryRows,
  buildSessionTeamGameHistoryRows,
} from '../utils/sessionPerformanceGameHistory';
import { buildSessionPlayerInsights, isGameInLadder } from '../utils/sessionPlayerInsights';
import {
  fetchSeasonOpponentRatingBaseline,
  summarizeOpponentRatingBaseline,
  fetchOpponentCurrentRatings,
} from '../services/sessionOpponentBaselineService';
import { fetchOpponentAllTimeRecords } from '../services/headToHeadService';
import { APP_PAGE_BLEED_ROOT, APP_PAGE_GUTTER } from '../utils/sessionMobileLayout';
import { SessionLadderToggle } from '../components/session/SessionLadderToggle';
import SessionSummaryTab from '../components/session/SessionSummaryTab';
import { isPegBoardSession } from '../utils/sessionTypeUtils';

const PLAYER_SEARCH_FIELD_CLASS =
  'form-input w-full min-w-0 rounded-md border-border-medium bg-field px-2.5 py-1.5 text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-primary sm:max-w-sm';

function playerRecordMatchesQuery(player, query) {
  const name = `${player?.first_name || ''} ${player?.last_name || ''} ${player?.name || ''}`.trim().toLowerCase();
  return name.includes(query);
}

function sessionPerformanceGameMatchesQuery(game, query, subjectPlayer = null) {
  const q = (query ?? '').trim().toLowerCase();
  if (!q) return true;
  if (subjectPlayer && playerRecordMatchesQuery(subjectPlayer, q)) return true;
  const people = [
    ...(game.teammates || []),
    ...(game.opponents || []),
    ...(game.team1Players || []),
    ...(game.team2Players || []),
  ];
  return people.some((player) => playerRecordMatchesQuery(player, q));
}

const PlayerSessionPerformancePage = ({ currentUser }) => {
  const { playerId, sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const clubIdFromNavigation = useMemo(
    () =>
      location.state?.clubId ||
      new URLSearchParams(location.search).get('clubId') ||
      null,
    [location.state?.clubId, location.search]
  );

  const [rankingData, setRankingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);
  const [playerInfo, setPlayerInfo] = useState(null);
  const [playerGamesData, setPlayerGamesData] = useState([]);
  const [sessionTeamGamesData, setSessionTeamGamesData] = useState([]);
  const [sessionGamesRaw, setSessionGamesRaw] = useState([]);
  const [eloChangesRaw, setEloChangesRaw] = useState([]);
  const [gamesScope, setGamesScope] = useState('mine');
  const [orderedGameResults, setOrderedGameResults] = useState([]);
  const [orderedMixedResults, setOrderedMixedResults] = useState([]);
  const [orderedLevelsResults, setOrderedLevelsResults] = useState([]);
  const [heroLadders, setHeroLadders] = useState(null);
  const [heroLaddersLoading, setHeroLaddersLoading] = useState(true);
  const [ladderView, setLadderView] = useState('mixed');
  // Independent from the hero/games ladderView above — the Summary tab's own
  // toggle defaults to 'all' since not every user cares about the mixed/levels split.
  const [summaryLadderView, setSummaryLadderView] = useState('all');
  const [opponentRatingBaseline, setOpponentRatingBaseline] = useState([]);
  const [opponentAllTimeRecords, setOpponentAllTimeRecords] = useState({});
  const [opponentAllTimeLoading, setOpponentAllTimeLoading] = useState(false);
  const [opponentCurrentRatings, setOpponentCurrentRatings] = useState({});
  const [playerSearchQuery, setPlayerSearchQuery] = useState('');

  const resolvedClubId = clubIdFromNavigation || sessionInfo?.club_id || null;
  const isPegBoard = isPegBoardSession(sessionInfo);

  useEffect(() => {
    const loadData = async () => {
      if (!sessionId || !playerId) return;

      setLoading(true);
      setError(null);
      setPlayerGamesData([]);
      setSessionTeamGamesData([]);
      setSessionGamesRaw([]);
      setEloChangesRaw([]);
      setRankingData([]);
      setOrderedGameResults([]);
      setOrderedMixedResults([]);
      setOrderedLevelsResults([]);

      try {
        // Fetch player info (include gender for levels ladder key)
        const { data: playerData, error: playerError } = await supabase
          .from('players')
          .select('id, first_name, last_name, gender')
          .eq('id', playerId)
          .single();

        if (playerError) throw playerError;
        setPlayerInfo(playerData);

        // Fetch session info
        const { data: sessionData, error: sessionError } = await supabase
          .from('sessions')
          .select(`
            id,
            name,
            session_date,
            club_id,
            clubs:club_id (
              id,
              name
            ),
            session_types:session_type_id (
              name
            )
          `)
          .eq('id', sessionId)
          .single();

        if (sessionError) throw sessionError;
        setSessionInfo(sessionData);

        const effectiveClubId = clubIdFromNavigation || sessionData?.club_id || null;
        if (!effectiveClubId) {
          throw new Error('Could not resolve club for this session.');
        }

        // Fetch all games + participant data
        const { data: sessionGames, error: gamesError } = await supabase
          .from('session_games')
          .select(`
            id,
            team_1_score,
            team_2_score,
            winning_team,
            created_at,
            players_session_games (
              player_id,
              team_number,
              result,
              pair_type,
              players (
                id,
                first_name,
                last_name,
                gender,
                is_guest,
                is_active,
                merged_at,
                guest_merge_declined_at
              )
            )
          `)
          .eq('session_id', sessionId)
          .order('created_at', { ascending: true });

        if (gamesError) throw gamesError;

        setSessionGamesRaw(sessionGames || []);

        const gameHistory = [];
        if (sessionGames) {
          sessionGames.forEach(game => {
            if (game.players_session_games) {
              game.players_session_games.forEach(playerGame => {
                const player = playerGame.players;
                if (player) {
                  gameHistory.push({
                    player_id: player.id,
                    player_first_name: player.first_name,
                    player_last_name: player.last_name,
                    gender: player.gender,
                    result: playerGame.result,
                    pair_type: playerGame.pair_type,
                    player_score: playerGame.team_number === 1 ? game.team_1_score : game.team_2_score,
                    opponent_score: playerGame.team_number === 1 ? game.team_2_score : game.team_1_score,
                    game_id: game.id,
                    created_at: game.created_at,
                  });
                }
              });
            }
          });
        }

        const isPegBoard = isPegBoardSession(sessionData);
        let eloChanges = [];

        if (!isPegBoard) {
          const { data: eloRows, error: eloError } = await supabase
            .from('elo_history')
            .select('player_id, session_game_id, elo_change, old_value, new_value, pair_type, game_type_id, game_types:game_type_id(name), season_id, created_at')
            .eq('session_id', sessionId)
            .eq('club_id', effectiveClubId);

          if (eloError) throw eloError;
          eloChanges = eloRows || [];
        }

        setEloChangesRaw(eloChanges);
        const gameHistoryEloMap = buildEloByGamePlayerMap(eloChanges);

        // Build per-player stats for rankings table
        const playerStats = {};
        gameHistory.forEach(game => {
          const pid = game.player_id;
          if (!playerStats[pid]) {
            playerStats[pid] = {
              player_id: pid,
              player_first_name: game.player_first_name,
              player_last_name: game.player_last_name,
              gender: game.gender,
              games: [],
              wins: 0,
              losses: 0,
            };
          }
          const s = playerStats[pid];
          s.games.push(game);
          if (game.result === 'win') s.wins += 1;
          if (game.result === 'loss') s.losses += 1;
        });

        const rankings = Object.values(playerStats).map(stat => {
          const pid = stat.player_id;
          const eloData = (eloChanges || []).filter(e => e.player_id === pid);

          let mixedDelta = 0;
          let levelsDelta = 0;

          if (eloData.length) {
            const isFemale = (stat.gender || '').toLowerCase().startsWith('f');
            const levelsTypeId = isFemale ? 3 : 2;
            const mixedEntries = eloData.filter(e => e.game_type_id === 1 || e.pair_type === 'mixed' || (e.game_types?.name || '').toLowerCase().includes('mixed'));
            mixedDelta = mixedEntries.reduce((sum, e) => sum + (e.elo_change || 0), 0);
            const levelsEntries = eloData.filter(e => e.game_type_id === levelsTypeId || e.pair_type === 'levels' || (e.game_types?.name || '').toLowerCase().includes('level'));
            levelsDelta = levelsEntries.reduce((sum, e) => sum + (e.elo_change || 0), 0);
          }

          const totalGames = stat.wins + stat.losses;
          const winPct = totalGames > 0 ? (stat.wins / totalGames) * 100 : 0;

          return {
            player_id: pid,
            name: `${stat.player_first_name || ''} ${stat.player_last_name || ''}`.trim(),
            gender: stat.gender,
            games: totalGames,
            wins: stat.wins,
            losses: stat.losses,
            winPct,
            recentForm: buildRecentFormString(stat.games),
            mixedDelta,
            levelsDelta,
          };
        }).sort((a, b) => b.winPct - a.winPct || (b.mixedDelta + b.levelsDelta) - (a.mixedDelta + a.levelsDelta));

        setRankingData(rankings.map((r, idx) => ({ ...r, rank: idx + 1 })));

        // Build ordered game results for this player (for per-ladder stats)
        const myGames = gameHistory
          .filter(g => g.player_id === playerId)
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

        const allResults = myGames.map(g => g.result);
        const mixedResults = myGames.filter(g => !g.pair_type || g.pair_type === 'mixed').map(g => g.result);
        const levelsResults = myGames.filter(g => g.pair_type === 'levels').map(g => g.result);

        setOrderedGameResults(allResults);
        setOrderedMixedResults(mixedResults);
        setOrderedLevelsResults(levelsResults);

        const sessionMeta = {
          sessionId,
          clubName: sessionData?.clubs?.name || '',
          sessionName: sessionData?.name || '',
        };

        if (sessionGames?.length > 0) {
          setPlayerGamesData(
            buildPlayerGameHistoryRows(sessionGames, playerId, sessionMeta, gameHistoryEloMap),
          );
          setSessionTeamGamesData(
            buildSessionTeamGameHistoryRows(sessionGames, sessionMeta, gameHistoryEloMap, playerId),
          );
        }
      } catch (err) {
        console.error('[PlayerSessionPerformance] Failed to load data', err);
        setError('Failed to load session performance. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [sessionId, playerId, clubIdFromNavigation]);

  // Build hero KPIs with the same club-level fallbacks as the Home last-session hero.
  useEffect(() => {
    if (isPegBoard || loading || !sessionId || !playerId || !resolvedClubId || !playerInfo) {
      if (!loading || isPegBoard) {
        setHeroLadders(null);
        setHeroLaddersLoading(false);
      }
      return;
    }

    let cancelled = false;
    setHeroLaddersLoading(true);

    const playerRow = rankingData.find((r) => r.player_id === playerId);

    (async () => {
      try {
        const snapshotRecap = await fetchSessionLeaderboardSnapshotRecap(
          sessionId,
          playerId,
          resolvedClubId
        ).catch(() => []);

        const mixedStats = summarizeLadderGameResults(orderedMixedResults);
        const levelsStats = summarizeLadderGameResults(orderedLevelsResults);
        const built = buildSessionLadderSnapshot({
          mixedStats,
          levelsStats,
          mixedEloDelta: playerRow?.mixedDelta ?? 0,
          levelsEloDelta: playerRow?.levelsDelta ?? 0,
          snapshotRecap,
          viewerGender: playerInfo?.gender ?? null,
        });

        await enrichSessionLadderSnapshot(built, {
          playerId,
          clubId: resolvedClubId,
          sessionId,
        });

        if (!cancelled) {
          setHeroLadders(built);
        }
      } catch (err) {
        console.warn('[PlayerSessionPerformance] Hero ladder enrichment failed:', err);
        if (!cancelled) {
          setHeroLadders(null);
        }
      } finally {
        if (!cancelled) {
          setHeroLaddersLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    loading,
    sessionId,
    playerId,
    resolvedClubId,
    playerInfo,
    orderedMixedResults,
    orderedLevelsResults,
    rankingData,
    isPegBoard,
  ]);

  const playerRow = useMemo(
    () => rankingData.find((r) => r.player_id === playerId),
    [rankingData, playerId]
  );

  const eloMap = useMemo(() => buildEloByGamePlayerMap(eloChangesRaw), [eloChangesRaw]);

  const summaryInsightsData = useMemo(
    () =>
      buildSessionPlayerInsights(sessionGamesRaw, eloMap, playerId, summaryLadderView, {
        levelsLabel: heroLadders?.levels?.toggleLabel ?? 'Levels',
      }),
    [sessionGamesRaw, eloMap, playerId, summaryLadderView, heroLadders?.levels?.toggleLabel]
  );

  // Stable opponent-id set across the whole session (independent of the toggle),
  // used to fetch all-time H2H records once rather than re-fetching per toggle change.
  const allSessionOpponentIds = useMemo(() => {
    const allLaddersInsights = buildSessionPlayerInsights(sessionGamesRaw, eloMap, playerId, 'all');
    return allLaddersInsights.opponentSessionRecords.map((o) => o.opponentId);
  }, [sessionGamesRaw, eloMap, playerId]);

  const resolvedSeasonId = eloChangesRaw?.[0]?.season_id ?? null;

  // Lazy season-wide "average opponent rating" baseline for the Summary tab's
  // opponent-difficulty card — separate from the main data load since it queries
  // outside this session (see sessionOpponentBaselineService.js).
  useEffect(() => {
    if (isPegBoard || !playerId || !resolvedClubId || !resolvedSeasonId || !sessionId) {
      setOpponentRatingBaseline([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const baseline = await fetchSeasonOpponentRatingBaseline({
          playerId,
          clubId: resolvedClubId,
          seasonId: resolvedSeasonId,
          excludeSessionId: sessionId,
        });
        if (!cancelled) setOpponentRatingBaseline(baseline);
      } catch (err) {
        console.warn('[PlayerSessionPerformance] Opponent rating baseline failed:', err);
        if (!cancelled) setOpponentRatingBaseline([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [playerId, resolvedClubId, resolvedSeasonId, sessionId, isPegBoard]);

  const opponentRatingBaselineSummary = useMemo(
    () => summarizeOpponentRatingBaseline(opponentRatingBaseline, isGameInLadder, summaryLadderView),
    [opponentRatingBaseline, summaryLadderView]
  );

  // Lazy all-time head-to-head per session opponent, for the "Opponents this session" list.
  useEffect(() => {
    if (!playerId || !resolvedClubId || !allSessionOpponentIds.length) {
      setOpponentAllTimeRecords({});
      return;
    }
    let cancelled = false;
    setOpponentAllTimeLoading(true);
    (async () => {
      try {
        const records = await fetchOpponentAllTimeRecords(playerId, allSessionOpponentIds, resolvedClubId);
        if (!cancelled) setOpponentAllTimeRecords(records);
      } catch (err) {
        console.warn('[PlayerSessionPerformance] All-time H2H fetch failed:', err);
        if (!cancelled) setOpponentAllTimeRecords({});
      } finally {
        if (!cancelled) setOpponentAllTimeLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [playerId, resolvedClubId, allSessionOpponentIds]);

  // Lazy current club+season ratings per session opponent (separate table from
  // session_games — see sessionOpponentBaselineService.js).
  useEffect(() => {
    if (isPegBoard || !resolvedClubId || !resolvedSeasonId || !allSessionOpponentIds.length) {
      setOpponentCurrentRatings({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const ratings = await fetchOpponentCurrentRatings(allSessionOpponentIds, resolvedClubId, resolvedSeasonId);
        if (!cancelled) setOpponentCurrentRatings(ratings);
      } catch (err) {
        console.warn('[PlayerSessionPerformance] Opponent current ratings fetch failed:', err);
        if (!cancelled) setOpponentCurrentRatings({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolvedClubId, resolvedSeasonId, allSessionOpponentIds, isPegBoard]);

  // Session-wide stats for "All" mode
  const sessionStats = useMemo(() => {
    if (loading || orderedGameResults.length === 0) return null;
    const wins = orderedGameResults.filter(r => r === 'win').length;
    const losses = orderedGameResults.filter(r => r === 'loss').length;
    const total = wins + losses;
    let maxWinStreak = 0;
    let cur = 0;
    for (const r of orderedGameResults) {
      if (r === 'win') { cur++; if (cur > maxWinStreak) maxWinStreak = cur; }
      else cur = 0;
    }
    return {
      wins,
      losses,
      winRatePct: total > 0 ? Math.round((wins / total) * 100) : null,
      maxWinStreak,
      mixedEloDelta: playerRow?.mixedDelta ?? 0,
      levelsEloDelta: playerRow?.levelsDelta ?? 0,
    };
  }, [loading, orderedGameResults, playerRow]);

  const handleBack = () => {
    if (resolvedClubId) {
      navigate(`/clubs/${resolvedClubId}?tab=performance`);
    } else {
      navigate(-1);
    }
  };

  const isSelf = !currentUser || currentUser.id === playerId;
  const gamesTabLabel = isSelf
    ? 'Games'
    : playerInfo?.first_name
      ? `${playerInfo.first_name}'s games`
      : 'Games';

  const subjectPlayerLabel = isSelf
    ? 'You'
    : (playerInfo?.first_name || 'Player');

  const onlyMyGames = gamesScope === 'mine';

  const gamesScopeTabs = useMemo(() => {
    const withCount = (label, count) => (count > 0 ? `${label} (${count})` : label);
    const mineLabel = isSelf
      ? 'My games'
      : `${playerInfo?.first_name || 'Player'}'s games`;
    return [
      { id: 'mine', label: withCount(mineLabel, playerGamesData.length) },
      { id: 'all', label: withCount('All session', sessionTeamGamesData.length) },
    ];
  }, [isSelf, playerInfo?.first_name, playerGamesData.length, sessionTeamGamesData.length]);

  const visibleGames = onlyMyGames ? playerGamesData : sessionTeamGamesData;
  const gamesRowLayout = onlyMyGames ? 'player' : 'sessionTeams';
  const playerSearchActive = playerSearchQuery.trim().length > 0;

  const filteredVisibleGames = useMemo(
    () => visibleGames.filter((game) =>
      sessionPerformanceGameMatchesQuery(game, playerSearchQuery, onlyMyGames ? playerInfo : null)
    ),
    [visibleGames, playerSearchQuery, onlyMyGames, playerInfo]
  );

  const filteredRankingData = useMemo(() => {
    const q = playerSearchQuery.trim().toLowerCase();
    if (!q) return rankingData;
    return rankingData.filter((row) => (row.name || '').toLowerCase().includes(q));
  }, [rankingData, playerSearchQuery]);

  const playerSearchField = (
    <MobileSearchField
      id="player-session-performance-search"
      aria-label="Search by player name"
      value={playerSearchQuery}
      onChange={(e) => setPlayerSearchQuery(e.target.value)}
      placeholder="Search players…"
      className={PLAYER_SEARCH_FIELD_CLASS}
    />
  );

  const tabs = useMemo(() => [
    {
      id: 'summary',
      label: 'Summary',
      content: (
        <SessionSummaryTab
          insights={summaryInsightsData}
          ladderView={summaryLadderView}
          onLadderViewChange={setSummaryLadderView}
          canToggleLadder={Boolean(heroLadders?.canToggleLadder)}
          mixedToggleLabel={heroLadders?.mixed?.toggleLabel ?? 'Mixed'}
          levelsToggleLabel={heroLadders?.levels?.toggleLabel ?? 'Levels'}
          hideRatingInsights={isPegBoard}
          opponentRatingBaseline={isPegBoard ? null : opponentRatingBaselineSummary}
          opponentAllTimeRecords={opponentAllTimeRecords}
          opponentAllTimeLoading={opponentAllTimeLoading}
          opponentCurrentRatings={opponentCurrentRatings}
          isSelf={isSelf}
          subjectPlayerLabel={subjectPlayerLabel}
          loading={loading}
          clubId={resolvedClubId}
        />
      ),
    },
    {
      id: 'games',
      label: gamesTabLabel,
      badge: visibleGames.length > 0 ? visibleGames.length : undefined,
      content: (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {playerSearchField}
            <SessionLadderToggle
              variant="light"
              ariaLabel="Game list scope"
              tabs={gamesScopeTabs}
              value={gamesScope}
              onChange={setGamesScope}
            />
          </div>
          {playerSearchActive && filteredVisibleGames.length === 0 ? (
            <p className="py-6 text-center text-sm text-text-secondary">
              No games match that player. Try another name or clear the search.
            </p>
          ) : (
            <GameHistoryTable
              key={gamesScope}
              games={filteredVisibleGames}
              pageSize={10}
              rowLayout={gamesRowLayout}
              hideSessionColumn
              hideFilters
              hideViewSessionLink
              hideSessionHeaders
              hideEloRatings={isPegBoard}
              clubId={resolvedClubId}
              subjectPlayerLabel={subjectPlayerLabel}
            />
          )}
        </div>
      ),
    },
    {
      id: 'rankings',
      label: 'Session rankings',
      badge: playerRow?.rank != null ? `#${playerRow.rank}` : undefined,
      content: (
        <>
          {loading ? (
            <div className="py-6 text-center text-text-muted">Loading rankings…</div>
          ) : rankingData.length === 0 ? (
            <div className="py-6 text-center text-text-muted">
              No ranking data available for this session yet.
            </div>
          ) : (
            <div className="space-y-4">
              {playerSearchField}
              {filteredRankingData.length === 0 ? (
                <p className="py-6 text-center text-sm text-text-secondary">
                  No players match that name. Try another search.
                </p>
              ) : (
                <SessionRankingsTable
                  rows={filteredRankingData}
                  highlightPlayerId={playerId}
                  isSelfView={isSelf}
                />
              )}
            </div>
          )}
        </>
      ),
    },
  ], [
    summaryInsightsData,
    summaryLadderView,
    opponentRatingBaselineSummary,
    opponentAllTimeRecords,
    opponentAllTimeLoading,
    opponentCurrentRatings,
    heroLadders?.canToggleLadder,
    heroLadders?.mixed?.toggleLabel,
    heroLadders?.levels?.toggleLabel,
    gamesTabLabel,
    visibleGames,
    filteredVisibleGames,
    filteredRankingData,
    playerSearchQuery,
    playerSearchActive,
    gamesScope,
    gamesScopeTabs,
    gamesRowLayout,
    resolvedClubId,
    loading,
    rankingData,
    playerId,
    isSelf,
    playerRow?.rank,
    subjectPlayerLabel,
    isPegBoard,
  ]);

  return (
    <div className={`min-h-screen bg-background text-text-primary ${APP_PAGE_BLEED_ROOT}`}>
      <div className={`max-w-app-shell mx-auto ${APP_PAGE_GUTTER} pt-3 md:pt-6 pb-8`}>
        <div className="mb-3 sm:mb-6">
          <BackButton onClick={handleBack}>Back</BackButton>
        </div>

        {error && (
          <Card variant="danger" size="small" className="mb-6">
            <Text className="text-sm text-danger-dark">{error}</Text>
          </Card>
        )}

        {/* Dark KPI hero */}
        <SessionPerformanceHero
          sessionInfo={sessionInfo}
          ladders={heroLadders}
          sessionStats={sessionStats}
          loading={loading || (!isPegBoard && heroLaddersLoading)}
          ladderView={ladderView}
          onLadderViewChange={setLadderView}
          variant={isPegBoard ? 'pegBoard' : 'competitive'}
        />

        {/* Tabbed: Your games | Session rankings */}
        <Card variant="default" size="large" className="!p-4 sm:!p-6 lg:!p-8">
          <Tabs tabs={tabs} defaultTab="summary" />
        </Card>
      </div>
    </div>
  );
};

export default PlayerSessionPerformancePage;
